import React, { useEffect, useState, useMemo } from "react"
import clone from "lodash/clone"
import find from "lodash/find"
import filter from "lodash/filter"

import AuthenticationTestEnsureTokenDoesNotExpire from "partners/components/authentication_test/token_not_expire"
import AuthenticationTestRefreshAccessToken from "partners/components/authentication_test/refresh_token"
import AuthenticationTestRetrieveAuthCode from "partners/components/authentication_test/retrieve_auth_code"
import AuthenticationTestRetrieveUserInfo from "partners/components/authentication_test/retrieve_user_info"
import AuthenticationTestRequestToken from "partners/components/authentication_test/request_token"
import ModularAuthSteps from "partners/components/authentication_test/modular_auth_steps"

// Parse OAuth callback params from URL
function getOAuthCallbackParams(): Record<string, string> | null {
  const params = new URLSearchParams(window.location.search)
  const callbackParams: Record<string, string> = {}

  // Common OAuth callback params
  const relevantParams = ["code", "state", "error", "error_description", "oauth_token", "oauth_verifier"]

  relevantParams.forEach(param => {
    const value = params.get(param)
    if (value) {
      callbackParams[param] = value
    }
  })

  return Object.keys(callbackParams).length > 0 ? callbackParams : null
}

interface Props {
  activation: {
    code: string
    code_verifier: string | null
  }
  activation_path: string
  refresh_token_path: string
  request_token_path: string
  retrieve_user_info_path: string
  service: {
    oauth_use_refresh_token: boolean
    authentication_type: string
    auth_flow_name: string | null
    module_name: string
  }
}

const AuthenticationTest = ({
  activation,
  activation_path,
  refresh_token_path,
  request_token_path,
  retrieve_user_info_path,
  service,
}: Props) => {
  const initialStatuses: { [key: string]: string } = { retrieveUserInfo: "" }
  if (["oauth", "oauth_pkce"].includes(service.authentication_type)) {
    initialStatuses.retrieveAuthCode = ""
    initialStatuses.requestToken = ""
  }
  if (["oauth_refresh_token", "oauth_pkce_refresh_token"].includes(service.authentication_type)) {
    initialStatuses.retrieveAuthCode = ""
    initialStatuses.requestToken = ""
    initialStatuses.refreshAccessToken = ""
    initialStatuses.tokenNotExpire = ""
  }
  const [statuses, setStatuses] = useState(initialStatuses)
  const [combinedStatus, setCombinedStatus] = useState("")
  const isShowing = !!activation
  const auth = activation

  useEffect(() => {
    const testStatusListener = window.addEventListener("statusReceived", event => {
      const statusesClone = clone(statuses)
      let newCombinedStatus = ""

      statusesClone[event.detail.componentKey] = event.detail.componentStatus

      if (find(statusesClone, value => value === "failing")) {
        newCombinedStatus = "failing"
      } else if (filter(statusesClone, value => value === "passing").length === Object.keys(statusesClone).length) {
        newCombinedStatus = "passing"
      }
      setStatuses(statusesClone)
      setCombinedStatus(newCombinedStatus)
    })

    return () => {
      window.removeEventListener("statusReceived", testStatusListener)
    }
  }, [])

  let titleText = "Authentication tests pending."
  if (combinedStatus === "failing") {
    titleText = "Authentication tests failed."
  } else if (combinedStatus === "passing") {
    titleText = "Authentication tests successful."
  }
  const isApiTokenAuth = service.authentication_type === "api_token_auth"
  const isOauth = ["oauth", "oauth_pkce"].includes(service.authentication_type)
  const isRefreshTokenAuth = ["oauth_refresh_token", "oauth_pkce_refresh_token"].includes(service.authentication_type)
  const isModularAuth = !!service.auth_flow_name

  // Get OAuth callback params once on mount
  const callbackParams = useMemo(() => getOAuthCallbackParams(), [])

  if (isModularAuth) {
    return <ModularAuthSteps channelModuleName={service.module_name} callbackParams={callbackParams} />
  }

  return (
    <div className={`publish-item heading ${combinedStatus}`}>
      <header>{titleText}</header>
      {isShowing && (isApiTokenAuth || isOauth || isRefreshTokenAuth) && (
        <>
          {(isOauth || isRefreshTokenAuth) && (
            <>
              <AuthenticationTestRetrieveAuthCode auth={auth} path={activation_path} service={service} />
              <AuthenticationTestRequestToken code={activation.code} codeVerifier={activation.code_verifier} path={request_token_path} service={service} />
            </>
          )}
          <AuthenticationTestRetrieveUserInfo
            path={retrieve_user_info_path}
            service={service}
            authentication_type={service.authentication_type}
          />
          {isRefreshTokenAuth && (
            <>
              <AuthenticationTestRefreshAccessToken path={refresh_token_path} service={service} />
              <AuthenticationTestEnsureTokenDoesNotExpire path={refresh_token_path} service={service} />
            </>
          )}
        </>
      )}
    </div>
  )
}

export default AuthenticationTest
